import { useAppStore } from '@/store/store';
import { selectShipmentSummary } from '@/store/selectors';
import { calcRevenueTon } from '@freight-cbm/shared-logic';
import { Tooltip } from '@/components/shared/Tooltip';
import { EmptyState } from '@/components/shared/EmptyState';
import { Calculator as CalculatorIcon } from 'lucide-react';

export function ResultBreakdown() {
  const summary = useAppStore(selectShipmentSummary);
  const mode = useAppStore((s) => s.mode);

  return (
    <div className="section-divider" data-testid="result-breakdown">
      <h3 className="section-title">Calculation Breakdown</h3>
      <div>
        {summary.totalPackages === 0 ? (
          <EmptyState
            icon={CalculatorIcon}
            compact
            title="Calculation breakdown appears here"
            description="Add packages to see volume weight, chargeable weight, and revenue ton calculations."
          />
        ) : (
          <>
            <Row label="Total Volume" value={`${summary.totalCBM.toFixed(4)} m³`} />
            <Row label="Total Weight" value={`${summary.grossWeightKg.toFixed(2)} kg`} />
            <Row
              label="Volume Weight"
              value={`${summary.dimWeightKg.toFixed(2)} kg`}
              sublabel={`(CBM × 1,000,000 ÷ divisor)`}
            />
            <Row
              label="Chargeable Weight"
              value={`${summary.chargeableWeightKg.toFixed(2)} kg`}
              helpText="The greater of actual weight and dimensional weight, used for billing."
              highlight
            />
            {mode === 'ocean-lcl' && (
              <Row label="Revenue Ton" helpText="The greater of CBM or metric tons — the billing unit for ocean LCL." value={`${calcRevenueTon(summary.totalCBM, summary.grossWeightKg).revenueTon.toFixed(4)}`} highlight />
            )}
            <Row label="Density" value={`${summary.cargoDensityKgPerCBM.toFixed(2)} kg/m³`} />
          </>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  sublabel,
  highlight,
  helpText,
}: {
  label: string;
  value: string;
  sublabel?: string;
  highlight?: boolean;
  helpText?: string;
}) {
  const labelSpan = <span>{label}</span>;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: highlight ? 'var(--space-3)' : 'var(--space-2) 0',
        marginBottom: highlight ? 'var(--space-2)' : 0,
        borderBottom: highlight ? 'none' : '1px solid var(--color-border)',
        borderRadius: highlight ? 'var(--radius-md)' : 0,
        background: highlight ? 'var(--color-accent-light)' : 'transparent',
        fontSize: highlight ? 'var(--font-size-md)' : 'var(--font-size-sm)',
        fontWeight: highlight ? 'var(--font-weight-extrabold)' : 'var(--font-weight-normal)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {helpText ? <Tooltip content={helpText}>{labelSpan}</Tooltip> : labelSpan}
        {sublabel && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {sublabel}
          </span>
        )}
      </div>
      <span style={{ fontFamily: 'var(--font-family-mono)', fontFeatureSettings: "'tnum'", letterSpacing: '-0.01em' }}>{value}</span>
    </div>
  );
}
